/**
 * EInvoiceDetailsForm Component
 * E-Fatura özel alanları (Profil Türü, Tip, Senaryo vb.)
 */

import React from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  EInvoiceProfileType,
  EInvoiceTypeCode,
  EInvoiceScenario,
  EInvoiceProfileTypeLabels,
  EInvoiceTypeCodeLabels,
  ProfileTypeToScenarioMap,
} from '../types/einvoice.types';
import { SectionTitle } from '../styled/InvoiceStyledComponents';

interface EInvoiceDetailsFormProps {
  profileType: EInvoiceProfileType;
  typeCode: EInvoiceTypeCode;
  scenario: EInvoiceScenario;
  onProfileTypeChange: (value: EInvoiceProfileType) => void;
  onTypeCodeChange: (value: EInvoiceTypeCode) => void;
  disabled?: boolean;
  isEInvoiceUser?: boolean;
  /**
   * Carinin e-Fatura mükellefi olup olmadığı (cari seçilince otomatik sorgulanır).
   * true  → e-Fatura mükellefi (TEMEL/Ticari seçilebilir, Ticari önerilir)
   * false → mükellef değil (E-Arşiv'e kilitli)
   * null/undefined → henüz sorgulanmadı (tüm seçenekler açık)
   */
  mukellef?: boolean | null;
  /** Mükellef sorgusu devam ediyor mu (bilgi rozeti için). */
  mukellefLoading?: boolean;
  /** Mükellef sorgusunda dönen ünvan (bilgi amaçlı). */
  mukellefTitle?: string;
}

export const EInvoiceDetailsForm: React.FC<EInvoiceDetailsFormProps> = ({
  profileType,
  typeCode,
  scenario,
  onProfileTypeChange,
  onTypeCodeChange,
  disabled = false,
  isEInvoiceUser = false,
  mukellef = null,
  mukellefLoading = false,
  mukellefTitle,
}) => {
  // Mükellef durumuna göre seçilebilir profil türleri:
  //  - mükellef değil → yalnızca E-Arşiv (kilitli)
  //  - mükellef       → E-Arşiv hariç (iki mükellef arası TEMEL/Ticari kullanılır)
  //  - bilinmiyor     → tüm seçenekler
  const profileOptions = Object.values(EInvoiceProfileType).filter((value) => {
    if (mukellef === false) return value === EInvoiceProfileType.EARSIV;
    if (mukellef === true) return value !== EInvoiceProfileType.EARSIV;
    return true;
  });
  const profileLocked = mukellef === false; // Mükellef değilse profil E-Arşiv'e sabit
  // Profil türü değiştiğinde senaryoyu otomatik güncelle
  const handleProfileTypeChange = (newProfileType: EInvoiceProfileType) => {
    console.log('🔄 [E-FATURA FORM] ProfileType değişti:', profileType, '->', newProfileType);
    onProfileTypeChange(newProfileType);
    // Senaryo otomatik olarak hesaplanacak (parent component'te)
  };

  const handleTypeCodeChange = (newTypeCode: EInvoiceTypeCode) => {
    console.log('🔄 [E-FATURA FORM] TypeCode değişti:', typeCode, '->', newTypeCode);
    onTypeCodeChange(newTypeCode);
  };

  return (
    <Box>
      <SectionTitle variant="h6" sx={{ fontSize: '0.85rem', mb: 1, display: 'none' }}>
        E-Fatura Detayları
      </SectionTitle>
      
      {/* Mükellef durumu bilgi rozeti (cari seçilince otomatik) */}
      {(mukellefLoading || mukellef === true || mukellef === false) && (
        <Box sx={{ mb: 1 }}>
          {mukellefLoading ? (
            <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ py: 0.25 }}>
              <Typography variant="caption">Cari e-Fatura mükellefiyeti sorgulanıyor…</Typography>
            </Alert>
          ) : mukellef === true ? (
            <Alert severity="success" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>e-Fatura mükellefi</strong>{mukellefTitle ? ` — ${mukellefTitle}` : ''} → <strong>Ticari</strong> fatura gönderilir.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <strong>e-Fatura mükellefi değil</strong> → <strong>e-Arşiv</strong> olarak düzenlenir (TEMEL/Ticari kullanılamaz).
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      <Grid container spacing={1.5}>
        {/* Fatura Profil Türü */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required disabled={disabled || profileLocked}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Fatura Profil Türü</InputLabel>
            <Select
              value={profileType}
              label="Fatura Profil Türü"
              onChange={(e) => handleProfileTypeChange(e.target.value as EInvoiceProfileType)}
              sx={{ fontSize: '0.875rem' }}
            >
              {profileOptions.map((value) => (
                <MenuItem key={value} value={value} sx={{ fontSize: '0.875rem' }}>
                  {EInvoiceProfileTypeLabels[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Fatura Tip Kodu */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required disabled={disabled}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Fatura Tip Kodu</InputLabel>
            <Select
              value={typeCode}
              label="Fatura Tip Kodu"
              onChange={(e) => handleTypeCodeChange(e.target.value as EInvoiceTypeCode)}
              sx={{ fontSize: '0.875rem' }}
            >
              {Object.entries(EInvoiceTypeCode).map(([key, value]) => (
                <MenuItem key={value} value={value} sx={{ fontSize: '0.875rem' }}>
                  {EInvoiceTypeCodeLabels[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Özel Durumlar için Uyarılar */}
        {profileType === EInvoiceProfileType.IHRACAT && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <strong>İhracat Faturası:</strong> İhracat faturaları için özel alanlar ve validasyonlar gereklidir.
              </Typography>
            </Alert>
          </Grid>
        )}
        
        {typeCode === EInvoiceTypeCode.TEVKIFAT && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <strong>Tevkifat Faturası:</strong> Tevkifat bilgilerini "Notlar ve Özet" bölümünden girmeyi unutmayın.
              </Typography>
            </Alert>
          </Grid>
        )}

        {typeCode === EInvoiceTypeCode.ISTISNA && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <strong>İstisna Faturası:</strong> %0 KDV'li kalem bulunduğu için fatura tipi otomatik "İstisna" seçildi.
                Gönderimden önce ilgili kalemler için <strong>KDV istisna sebep kodunu</strong> (örn. 301, 350…) girmeniz gerekebilir.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

